import { useState } from "react";
import { Users, Trash2, FlaskConical, BadgeCheck, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { DeleteUserDialog } from "@/components/DeleteUserDialog";

const FILTERS = [["all", "الكل"], ["real", "حقيقي"], ["test", "اختباري"], ["paid", "مشترك"]];

const Flag = ({ on, yes, no, Icon }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${on ? "bg-emerald-500/15 text-emerald-300" : "bg-muted text-muted-foreground"}`}>
    {Icon && <Icon className="w-3 h-3" />}{on ? yes : no}
  </span>
);

/** العملاء المسجّلون والمشتركون في قسم واحد، مع علامة حقيقي/اختباري قابلة للتبديل وحذف نهائي. */
export const AdminCustomers = ({ leads = [], onChanged }) => {
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState("");
  const rows = leads.filter((l) => filter === "all" || (filter === "real" && !l.is_test) || (filter === "test" && l.is_test) || (filter === "paid" && l.paid));
  const real = leads.filter((l) => !l.is_test).length;
  const paid = leads.filter((l) => l.paid && !l.is_test).length;

  const setFlag = async (l, value) => {
    setBusy(l.id);
    try { await api.put(`/admin/users/${l.id}/test-flag`, { is_test: value }); toast.success(value === null ? "عاد للتصنيف التلقائي" : value ? "عُلّم كاختباري" : "عُلّم كحقيقي"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التعديل"); }
    finally { setBusy(""); }
  };
  const purgeTests = async () => {
    const n = leads.length - real;
    if (!n) return toast.info("لا توجد بيانات اختبارية");
    if (!window.confirm(`حذف ${n} حساب اختباري مع كل بياناتهم + مشاريع الاختبار (TEST_…) والسجلات اليتيمة؟ الحقيقيون يبقون. لا يمكن التراجع.`)) return;
    setBusy("purge");
    try { const { data } = await api.post("/admin/users/purge-test"); toast.success(`تم التنظيف: ${data.users} مستخدم، ${data.projects} مشروع، ${data.content} عنصر — المتبقي ${data.remaining_users} مستخدم`); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التنظيف"); }
    finally { setBusy(""); }
  };
  const [target, setTarget] = useState(null);
  const remove = async () => {
    const l = target;
    setBusy(l.id);
    try { const { data } = await api.delete(`/admin/users/${l.id}`); toast.success(`حُذف نهائيًا: ${data.deleted_accounts} حساب، ${data.deleted_projects} مشروع، ${data.deleted_content} عنصر محتوى`); setTarget(null); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
    finally { setBusy(""); }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="customers-admin">
      <DeleteUserDialog target={target} busy={!!target && busy === target.id} onConfirm={remove} onClose={() => setTarget(null)} />
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Users className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-bold">العملاء ({leads.length})</h2>
        <span className="text-xs text-muted-foreground" data-testid="customers-summary">حقيقي {real} · اختباري {leads.length - real} · مشترك مدفوع {paid}</span>
        <div className="ms-auto flex flex-wrap gap-1 items-center">
          <Button size="sm" variant="outline" className="h-7 text-xs text-destructive border-destructive/40 hover:bg-destructive/10 gap-1 me-2" disabled={busy === "purge"} onClick={purgeTests} data-testid="purge-test-btn">
            {busy === "purge" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />} احذف كل البيانات الاختبارية
          </Button>
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`customers-filter-${k}`}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${filter === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>{label}</button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">التصنيف تلقائي من البريد/الاسم (test, qa, demo, reviewer…) ويمكنك تثبيته يدويًا من الزر؛ «مشترك» تعني باقة مدفوعة فعّالة.</p>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">لا يوجد عملاء في هذا التصنيف.</p> : (
        <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-muted-foreground text-xs border-b border-border">
              <th className="text-start py-2 font-medium">البريد</th>
              <th className="text-start py-2 font-medium">الجوال</th>
              <th className="text-start py-2 font-medium">الاسم</th>
              <th className="text-start py-2 font-medium">النوع</th>
              <th className="text-start py-2 font-medium">الباقة</th>
              <th className="text-start py-2 font-medium">الكوبون</th>
              <th className="text-start py-2 font-medium">مشاريع</th>
              <th className="text-start py-2 font-medium">التاريخ</th>
              <th className="text-start py-2 font-medium">إجراء</th>
            </tr></thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} className={`border-b border-border/50 ${l.is_test ? "opacity-70" : ""}`} data-testid={`customer-${l.id}`}>
                  <td className="py-2" dir="ltr">{l.email}</td>
                  <td className="py-2" dir="ltr">{l.phone || "—"}</td>
                  <td className="py-2">{l.name || "—"}</td>
                  <td className="py-2"><Flag on={!l.is_test} yes="حقيقي" no="اختباري" Icon={l.is_test ? FlaskConical : BadgeCheck} />{l.flag_manual && <span className="text-[10px] text-muted-foreground ms-1">يدوي</span>}</td>
                  <td className="py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${l.paid ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>{l.plan}{l.paid ? " · مشترك" : ""}</span></td>
                  <td className="py-2 text-xs" dir="ltr">{l.coupon || "—"}</td>
                  <td className="py-2 text-xs">{l.projects}</td>
                  <td className="py-2 text-muted-foreground text-xs" dir="ltr">{l.created_at ? String(l.created_at).slice(0, 10) : "—"}</td>
                  <td className="py-2">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === l.id} onClick={() => setFlag(l, !l.is_test)} data-testid={`toggle-test-${l.id}`}>
                        {l.is_test ? "اعتبره حقيقيًا" : "اعتبره اختباريًا"}
                      </Button>
                      {l.flag_manual && <Button size="icon" variant="ghost" className="h-7 w-7" title="تصنيف تلقائي" disabled={busy === l.id} onClick={() => setFlag(l, null)} data-testid={`auto-flag-${l.id}`}><RotateCcw className="w-3.5 h-3.5" /></Button>}
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" disabled={busy === l.id} onClick={() => setTarget(l)} data-testid={`delete-user-${l.id}`}>
                        {busy === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
